/* eslint-disable max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import i18n from 'i18next';
import { useQueryClient } from '@tanstack/react-query';
import { addToast, useModals } from 'core/notifications';
import { useLocation, useParams } from 'react-router-dom';

import truncate from 'lodash/truncate';
import find from 'lodash/find';
import getAxiosConfig from '../helpers/getAxiosConfig';

import {
  getMenuItems,
  getPublishModal,
  history,
  addTreeItemSelectorModal,
  ListEmpty,
  getSelectOptions,
  useGlobalQueries,
  useIsFeatureEnabled,
} from '@/exceed';

import { urlSearch } from '@/search';

import { hasPermission } from '../helpers/getVideoPermissions';
import addNewOnDemandTopicModal from '../helpers/addNewOnDemandTopicModal';
import addNewOnDemandVideoModal from '../helpers/addNewOnDemandVideoModal';
import getDeleteItemModal from '../helpers/getDeleteItemModal';
import getNewLiveVideoModal from '../helpers/getNewLiveVideoModal';
import getOrganizationVideoPermissionsForUser from '../helpers/getOrganizationVideoPermissionsForUser';
import getSelectedVideoStatus from '../helpers/getSelectedVideoStatus';
import getVideoMenuItems from '../helpers/getVideoMenuItems';
import getVideoContentStrings from '../helpers/getVideoContentStrings';
import handlePublish from '../helpers/handlePublish';
import handleReorderVideosTree from '../helpers/handleReorderVideosTree';
import handleMoveItem from '../helpers/handleMoveItem';
import handleAdminVideoAxiosError from '../helpers/handleAdminVideoAxiosError';
import getVideosTopicsTree from '../helpers/getVideosTopicsTree';
import getCurrentTreeContentParams from '../helpers/getCurrentTreeContentParams';
import getCurrentTreeContentTransform from '../helpers/getCurrentTreeContentTransform';
import getCurrentTreeBreadcrumbs from '../helpers/getCurrentTreeBreadcrumbs';
import hasVideoHomeEditorPermission from '../helpers/hasVideoHomeEditorPermission';

import useCurrentTreeContent from '../hooks/useCurrentTreeContent';
import useOrganizationVideoPermissions from '../hooks/useOrganizationVideoPermissions';
import useVideosAdminTree from '../hooks/useVideosAdminTree';

import VideosAdminContent from '../components/videosAdminContent';

const VideosAdminContentContainer = ({ isTopicsPage = false, videoVersion }) => {
  const params = useParams();
  const location = useLocation();
  const { addModal } = useModals();
  const isInLiveMode = params.type === 'live';
  const additionalFeaturesEnabled = useIsFeatureEnabled('videos_additional_features');

  const hasActiveSearch = () => {
    const urlParams = urlSearch.getParams(location.search);
    urlParams.delete('status');
    const search = urlParams.toString();

    return search.length > 0;
  };

  const isInSearchContext = hasActiveSearch();

  const [isShowingLoadingWheel, setIsShowingLoadingWheel] = useState(false);

  const { currentTreeContent, currentTreeContentKey } = useCurrentTreeContent({
    isTopicsPage,
    isInSearchContext,
    setIsShowingLoadingWheel,
  });

  const { videosAdminTree, flattenedVideosAdminTree } = useVideosAdminTree({ isTopicsPage });
  const globalQueries = useGlobalQueries();
  const { authority, locale } = globalQueries.currentUser.data;
  const organizationPermissions = useOrganizationVideoPermissions();

  const permissionsParams = {
    authority,
    flattenedVideosAdminTree,
    organizationPermissions,
    params,
  };

  const currentTreeBreadcrumbs = flattenedVideosAdminTree.data
    ? getCurrentTreeBreadcrumbs(permissionsParams)
    : [];

  const isContentLoading =
    currentTreeContent.isFetching ||
    currentTreeContent.isPlaceholderData ||
    videosAdminTree.isFetching;

  const queryClient = useQueryClient();

  const handleOnDragEnd = ({ rows, destination, source }) => {
    handleReorderVideosTree({
      destination,
      params,
      rows,
      source,
      currentTreeContentKey,
      queryClient,
    });
  };

  const handleImportVideoButtonClick = () => {
    // Todo: wire up import modal
  };

  const getNewItemModelDefaults = ({ id }) => {
    const { id: parentId, orgId } = params;

    return {
      locale,
      ...(id && { content_list_item_id: Number(id) }),
      organization_id: Number(orgId),
      ...(parentId && { parent_list_item_id: Number(parentId) }),
    };
  };

  const handleAddNewTopicButtonClick = async ({ id } = {}) => {
    const model = getNewItemModelDefaults({ id });
    const { type } = await addNewOnDemandTopicModal(model, globalQueries);

    if (type !== 'submit') return;

    await videosAdminTree.refetch();
    await currentTreeContent.refetch();
  };

  const handleAddNewVideoButtonClick = async ({ id } = {}) => {
    const model = getNewItemModelDefaults({ id });
    const { payload, type } = await addNewOnDemandVideoModal(model, globalQueries);

    if (type !== 'submit') return;

    await currentTreeContent.refetch();
    history.push(`/videos/organizations/${params.orgId}/on-demand/${payload.id}/compose`);
  };

  const handleOnActionItemClick = ({ action, item }) => {
    switch (action) {
      case 'import':
        return handleImportVideoButtonClick();
      case 'new-video':
        return handleAddNewVideoButtonClick({ id: item.id });
      case 'new-topic':
        return handleAddNewTopicButtonClick({ id: item.id });
      default:
    }
  };

  const handleAddNewLiveVideoButtonClick = async () => {
    const { orgId } = params;
    const model = { organization_id: Number(orgId), locale };
    const { payload, type } = await addModal(getNewLiveVideoModal(model, globalQueries));

    if (type !== 'submit' || !payload.id) return;

    history.push(`/videos/organizations/${orgId}/live/${payload.id}/compose`);

    await currentTreeContent.refetch();
  };

  const handlePublishItemModal = async ({ item }) => {
    const { id, name, title } = item.data || item;
    const { payload, type } = await addModal(getPublishModal({ id }));

    if (type !== 'submit') return;

    await handlePublish({ isInLiveMode, ...payload });
    addToast(i18n.t('publish-submitted-toast', { title: truncate(title || name, { length: 50 }) }));
  };

  const handleShowMoveToModal = ({ item }) => {
    const { orgId } = params;

    const selectOptions = getSelectOptions(
      getOrganizationVideoPermissionsForUser({ ...globalQueries, organizationPermissions }),
    );

    const defaultSelectValue = find(
      selectOptions,
      (selectOption) => selectOption.id === Number(orgId),
    );

    if (!item.data) {
      Object.assign(item, { data: { ...item } });
    }

    const options = {
      defaultSelectValue,
      noSearchResultsText: i18n.t('no-results'),
      selectOptions,
      fetchTree: ({ organizationId = orgId } = {}) => getVideosTopicsTree({ organizationId }),
      treeTopName: i18n.t('videos'),
      getIsTreeTopEnabled: (organizationId) =>
        hasVideoHomeEditorPermission({ ...globalQueries, organizationId, organizationPermissions }),
      searchPlaceholder: i18n.t('search-videos'),
      actionText: i18n.t('move'),
      modalOptions: {
        title: i18n.t('move-item', { item: item.data.name }),
        body: i18n.t('move-to-helper-text'),
      },
    };

    addTreeItemSelectorModal({
      item,
      options,
      onActionButtonClick: ({ organizationId = orgId, ...rest }) =>
        handleMoveItem({ currentTreeContent, organizationId, params, ...rest }),
    });
  };

  // const handleAddExistingVideoButtonClick = () => {
  //   // TODO: add modal
  // };

  const handleDeleteButtonClicked = async ({ type, id }) => {
    const { deleteBaseApiUrl } = getVideoContentStrings({ type });

    const url = `${deleteBaseApiUrl}/${id}`;

    try {
      await axios.delete(url);
    } catch (error) {
      return handleAdminVideoAxiosError(error);
    }

    const isInsideDeletedTopic = id === params.id;

    if (!isInsideDeletedTopic) return currentTreeContent.refetch();

    const rootUrl = `/videos/organizations/${params.orgId}/on-demand`;
    const { ancestry } = flattenedVideosAdminTree.data.find((item) => item.id === Number(id));

    history.push(ancestry ? `${rootUrl}/topics/${ancestry.at(-1).id}` : rootUrl);
  };

  const handleShowDeleteItemModal = ({ item }) => {
    const { id } = item;
    const type = isInLiveMode ? 'live' : item.type || item.data.type;

    const { deleteTitle, deleteBody } = getVideoContentStrings({ type });

    getDeleteItemModal({
      title: deleteTitle,
      body: deleteBody,
      onDeleteButtonClick: () => handleDeleteButtonClicked({ type, id }),
    });
  };

  const handleDuplicateItem = async ({ item }) => {
    const { id } = item;
    const type = isInLiveMode ? 'live' : 'video';

    const options = {
      live: {
        param: 'live_videos',
        body: null,
      },
      video: {
        param: 'videos',
        body: { data: { organization_id: Number(params.orgId) } },
      },
    };

    const { param, body } = options[type];

    const url = `/api/admin/${param}/${id}/duplicate`;

    try {
      await axios.post(url, body);
      await currentTreeContent.refetch();
    } catch (error) {
      handleAdminVideoAxiosError(error);
    }
  };

  const handleCreateShortcut = async ({ item }) => {
    const { id } = item;

    try {
      await axios(getAxiosConfig('create-shortcut', { id }));
      await currentTreeContent.refetch();
    } catch (error) {
      handleAdminVideoAxiosError(error);
    }
  };

  const handleOnKebabMenuItemClick = ({ action, item }) => {
    switch (action) {
      case 'publish':
        return handlePublishItemModal({ item });
      case 'delete':
        return handleShowDeleteItemModal({ item });
      case 'moveTo':
        return handleShowMoveToModal({ item });
      case 'duplicate':
        return handleDuplicateItem({ item });
      case 'create_shortcut':
        return handleCreateShortcut({ item });
      default:
        break;
    }
  };

  const handleOnContentActionClick = ({ action, item }) => {
    switch (action) {
      case 'publish':
        return handlePublishItemModal({ item });
      case 'duplicate':
        return handleDuplicateItem({ item });
      case 'delete':
        return handleShowDeleteItemModal({ item });
      default:
        break;
    }
  };

  const handleToggleButtonsChange = async (value) => {
    if (value === getSelectedVideoStatus()) return;

    urlSearch.setValue('status', value);
  };

  const getListHeaderAddMenuItems = () => {
    if (isInLiveMode) {
      return getMenuItems([
        {
          icon: 'liveVideo',
          key: 'new-live-video',
          onClick: handleAddNewLiveVideoButtonClick,
        },
        ...(additionalFeaturesEnabled
          ? [
              {
                icon: 'upload',
                key: 'import',
                onClick: handleImportVideoButtonClick,
              },
            ]
          : []),
      ]);
    }

    return getMenuItems([
      {
        icon: 'folderOutline',
        key: 'new-topic',
        onClick: handleAddNewTopicButtonClick,
      },
      {
        icon: 'playCircleOutline',
        key: 'new-video',
        onClick: handleAddNewVideoButtonClick,
      },
      // {
      //   icon: 'search',
      //   key: 'existing-video',
      //   onClick: handleAddExistingVideoButtonClick,
      // },
      ...(additionalFeaturesEnabled
        ? [
            {
              icon: 'upload',
              key: 'import',
              onClick: handleImportVideoButtonClick,
            },
          ]
        : []),
    ]);
  };

  const getListHeaderTitle = () => {
    const resultsCount = currentTreeContent.data.items?.length;

    return isInSearchContext ? i18n.t('results-count', { count: resultsCount }) : i18n.t('content');
  };

  const getListHeaderColumns = () => {
    if (isInSearchContext) return [];

    return isInLiveMode ? [i18n.t('start-date')] : [i18n.t('last-updated'), i18n.t('owner')];
  };

  const getEmptyStateAddMenuItems = () => {
    if (isInLiveMode) {
      return getMenuItems([
        {
          icon: 'liveVideo',
          key: 'new-live-video',
          onClick: handleAddNewLiveVideoButtonClick,
        },
      ]);
    }

    return getMenuItems([
      {
        icon: 'folderOutline',
        key: 'new-topic',
        onClick: handleAddNewTopicButtonClick,
      },
      {
        icon: 'playCircleOutline',
        key: 'new-video',
        onClick: handleAddNewVideoButtonClick,
      },
    ]);
  };

  const getLastBreadcrumbMenuItems = () => {
    const lastCrumb = currentTreeBreadcrumbs[currentTreeBreadcrumbs.length - 1] || {
      id: 0,
    };

    const items = getVideoMenuItems({
      item: lastCrumb,
      onKebabMenuItemClick: handleOnKebabMenuItemClick,
      params,
      videoVersion,
    });

    return getMenuItems(items);
  };

  const getListHeaderToggleButtons = () =>
    isInLiveMode
      ? [
          { label: i18n.t('open'), value: 'open' },
          { label: i18n.t('closed'), value: 'closed' },
          { label: i18n.t('canceled'), value: 'canceled' },
        ]
      : [];

  const getShouldDisableDragging = () => {
    if (isInLiveMode) return true;

    if (!isTopicsPage) {
      return !hasPermission({ permission: 'isVideoHomeEditor', ...permissionsParams });
    }

    const topicId = params.id;
    const treeItem = find(flattenedVideosAdminTree.data, (item) => item.id === Number(topicId));

    return (
      videosAdminTree.status === 'success' &&
      !hasPermission({
        permission: 'hasAccessToReorderWithinContent',
        ...permissionsParams,
        item: treeItem,
      })
    );
  };

  const shouldShowLoadingWheel = () => isContentLoading || isShowingLoadingWheel;

  const shouldShowEmptyState = () => {
    if (isInSearchContext) return false;

    const contentCount = Object.keys(currentTreeContent.data.items).length;

    // this will be 1 as there's always a root object in here
    return contentCount === 1 && !shouldShowLoadingWheel();
  };

  const shouldShowSearchResults = () => isInSearchContext && !shouldShowLoadingWheel();

  const shouldShowListView = () => {
    const hasRowItems = Boolean(currentTreeContent.data.items?.root?.children?.length);

    return !isInSearchContext && hasRowItems && !shouldShowLoadingWheel();
  };

  const listViewShouldShow = shouldShowListView();
  const searchResultsShouldShow = shouldShowSearchResults();
  const emptyStateShouldShow = shouldShowEmptyState();
  const loadingWheelShouldShow = shouldShowLoadingWheel();
  const shouldShowBreadCrumbs = isTopicsPage && !searchResultsShouldShow;
  const shouldShowListAddMenu = !searchResultsShouldShow;
  const listHeaderTitle = getListHeaderTitle();
  const listHeaderColumns = getListHeaderColumns();
  const lastBreadcrumbMenuItems = getLastBreadcrumbMenuItems();
  const listHeaderToggleButtonsValue = getSelectedVideoStatus();

  const isVideoHomeEditor = hasPermission({
    permission: 'isVideoHomeEditor',
    ...permissionsParams,
  });

  const isOnDemandHomePage = !isInLiveMode && !isTopicsPage;

  const shouldDisableOnDemandTopLevelCreation = isOnDemandHomePage
    ? !isVideoHomeEditor
    : !hasPermission({ permission: 'hasAccessToCreateWithinContent', ...permissionsParams });

  const shouldDisableTopLevelCreation = isInLiveMode
    ? !hasPermission({ permission: 'isLiveVideoCreator', ...permissionsParams })
    : shouldDisableOnDemandTopLevelCreation;

  const listEmptyComponent =
    listHeaderToggleButtonsValue === 'closed' ? (
      <ListEmpty
        title={i18n.t('no-closed-live-videos')}
        body={i18n.t('once-live-videos-have-closed')}
      />
    ) : (
      <ListEmpty
        title={i18n.t('no-content-available')}
        body={i18n.t('no-topics-videos')}
        buttonText={i18n.t('add-content')}
        hasAddMenu={listHeaderToggleButtonsValue !== 'canceled'}
        addMenuItems={getEmptyStateAddMenuItems()}
        isButtonDisabled={shouldDisableTopLevelCreation}
      />
    );

  return (
    <VideosAdminContent
      isVideoHomeEditor={isVideoHomeEditor}
      isOnDemandHomePage={isOnDemandHomePage}
      shouldDisableTopLevelCreation={shouldDisableTopLevelCreation}
      isInLiveMode={isInLiveMode}
      shouldDisableDrag={getShouldDisableDragging()}
      shouldShowListView={listViewShouldShow}
      shouldShowEmptyState={emptyStateShouldShow}
      shouldShowSearchResults={searchResultsShouldShow}
      shouldShowLoadingWheel={loadingWheelShouldShow}
      shouldShowBreadCrumbs={shouldShowBreadCrumbs}
      onDragEnd={handleOnDragEnd}
      onKebabMenuItemClick={handleOnKebabMenuItemClick}
      onActionItemClick={handleOnActionItemClick}
      onContentActionClick={handleOnContentActionClick}
      emptyStateAddMenuItems={getEmptyStateAddMenuItems()}
      listHeaderAddMenuItems={getListHeaderAddMenuItems()}
      currentTreeContent={currentTreeContent.data}
      breadcrumbs={currentTreeBreadcrumbs}
      lastBreadcrumbMenuItems={lastBreadcrumbMenuItems}
      shouldShowListAddMenu={shouldShowListAddMenu}
      listHeaderTitle={listHeaderTitle}
      listHeaderColumns={listHeaderColumns}
      listHeaderToggleButtons={getListHeaderToggleButtons()}
      listHeaderToggleButtonsValue={listHeaderToggleButtonsValue}
      onToggleButtonsChange={handleToggleButtonsChange}
      listEmptyComponent={listEmptyComponent}
      params={params}
      setIsShowingLoadingWheel={setIsShowingLoadingWheel}
    />
  );
};

VideosAdminContentContainer.propTypes = {
  isTopicsPage: PropTypes.bool,
};

export default VideosAdminContentContainer;
